import { useEffect } from 'react';
import { FolderPlus, FolderInput, ShoppingBag, Trash2, X, Shirt } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';
import { useHaptics } from '@/hooks/useHaptics';
import { useSetMultiSelectActive } from '@/contexts/MultiSelectContext';

interface MultiSelectActionBarProps {
  selectedCount: number;
  onAddToCollection?: () => void;
  onMoveToCollection?: () => void;
  onMarkAsPurchased?: () => void;
  onDelete?: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onExit: () => void;
  onTryOn?: () => void;
  totalItems: number;
}

export function MultiSelectActionBar({
  selectedCount,
  onAddToCollection,
  onMoveToCollection,
  onMarkAsPurchased,
  onDelete,
  onSelectAll,
  onDeselectAll,
  onExit,
  onTryOn,
  totalItems,
}: MultiSelectActionBarProps) {
  const haptics = useHaptics();
  const setMultiSelectActive = useSetMultiSelectActive();
  const allSelected = selectedCount === totalItems && totalItems > 0;

  // Signal BottomNav to hide
  useEffect(() => {
    setMultiSelectActive(true);
    return () => setMultiSelectActive(false);
  }, [setMultiSelectActive]);

  const handleAction = (action: () => void) => {
    haptics.light();
    action();
  };

  const hasAnyAction = onAddToCollection || onMoveToCollection || onMarkAsPurchased || onDelete || onTryOn;

  return (
    <div
      className={cn(
        "fixed z-50 transition-all duration-300 ease-out",
        selectedCount < 0 && "translate-y-[calc(100%+2rem+env(safe-area-inset-bottom))]"
      )}
      data-nav-mode="dark"
      style={{
        bottom: Capacitor.isNativePlatform()
          ? 'env(safe-area-inset-bottom, 1rem)'
          : '1rem',
        left: '1rem',
        right: '1rem',
      }}
    >
          <div className="nav-glass text-white rounded-[28px] p-3 flex flex-col gap-3" style={{ transition: 'none' }}>
            {/* Selection header */}
            <div className="flex items-center justify-between px-1">
              <span className="text-base font-semibold">
                {selectedCount} selected
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleAction(allSelected ? onDeselectAll : onSelectAll)}
                  className="text-sm font-medium opacity-70 hover:opacity-100 transition-opacity"
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
                <button
                  onClick={() => handleAction(onExit)}
                  className="p-1.5 -mr-1 rounded-full hover:bg-white/10 transition-colors"
                  aria-label="Exit selection mode"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Action buttons */}
            {hasAnyAction && (
              <div className="flex gap-2">
                {onTryOn && (
                  <button
                    onClick={() => handleAction(onTryOn)}
                    disabled={selectedCount === 0 || selectedCount > 6}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl transition-all font-medium text-sm",
                      selectedCount === 0 || selectedCount > 6
                        ? "bg-white/10 opacity-40 cursor-not-allowed"
                        : "bg-primary/20 hover:bg-primary/30 active:scale-[0.98]"
                    )}
                  >
                    <Shirt className="w-5 h-5" />
                    <span>Try On</span>
                  </button>
                )}

                {onAddToCollection && (
                  <button
                    onClick={() => handleAction(onAddToCollection)}
                    disabled={selectedCount === 0}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl transition-all font-medium",
                      selectedCount === 0
                        ? "bg-white/10 opacity-40 cursor-not-allowed"
                        : "bg-white/15 hover:bg-white/20 active:scale-[0.98]"
                    )}
                  >
                    <FolderPlus className="w-5 h-5" />
                    <span className="text-sm">Add to</span>
                  </button>
                )}

                {onMoveToCollection && (
                  <button
                    onClick={() => handleAction(onMoveToCollection)}
                    disabled={selectedCount === 0}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl transition-all font-medium",
                      selectedCount === 0
                        ? "bg-white/10 opacity-40 cursor-not-allowed"
                        : "bg-white/15 hover:bg-white/20 active:scale-[0.98]"
                    )}
                  >
                    <FolderInput className="w-5 h-5" />
                    <span className="text-sm">Move</span>
                  </button>
                )}

                {onMarkAsPurchased && (
                  <button
                    onClick={() => handleAction(onMarkAsPurchased)}
                    disabled={selectedCount === 0}
                    className={cn(
                      "flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl transition-all font-semibold",
                      selectedCount === 0
                        ? "bg-white/10 opacity-40 cursor-not-allowed"
                        : "bg-white/15 hover:bg-white/20 active:scale-[0.98]"
                    )}
                  >
                    <ShoppingBag className="w-5 h-5" />
                  </button>
                )}

                {onDelete && (
                  <button
                    onClick={() => handleAction(onDelete)}
                    disabled={selectedCount === 0}
                    className={cn(
                      "flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl transition-all font-semibold",
                      selectedCount === 0
                        ? "bg-white/10 opacity-40 cursor-not-allowed"
                        : "bg-white/15 hover:bg-white/20 active:scale-[0.98]"
                    )}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
    </div>
  );
}
